import React, { useCallback, useMemo } from 'react'
import { Dialog } from 'primereact/dialog'
import { useReconnection } from './hooks/useReconnection'
import { RECONNECTION_CONFIG } from './config/reconnection.config'
import ReconnectionSuccess from './components/ReconnectionSuccess'
import ReconnectionManual from './components/ReconnectionManual'
import ReconnectionProgress from './components/ReconnectionProgress'
import './styles.scss'

/**
 * Main ReconnectionDialog component
 * Handles automatic reconnection to virtual machines with improved architecture
 */
const ReconnectionDialog = ({ visible, vmId, displayName, onReconnect, onClose, isConnected }) => {
  // Use custom hook for reconnection logic
  const { state, retry, reset, stop } = useReconnection({
    visible,
    isConnected,
    onReconnect,
    onClose,
  })

  // Memoized dialog props to prevent unnecessary re-renders
  const dialogProps = useMemo(
    () => ({
      visible,
      onHide: () => {}, // Prevent closing with ESC or click outside
      header: `Reconexión - ${displayName}`,
      modal: true,
      closable: false,
      draggable: false,
      resizable: false,
      style: { width: RECONNECTION_CONFIG.DIALOG_WIDTH },
      className: 'reconnection-dialog',
      'aria-labelledby': 'reconnection-dialog-title',
      'aria-describedby': 'reconnection-dialog-description',
    }),
    [visible, displayName]
  )

  // Memoized retry handler
  const handleRetry = useCallback(() => {
    retry()
  }, [retry])

  // Memoized close handler
  const handleClose = useCallback(() => {
    stop()
    reset()
    onClose()
  }, [stop, reset, onClose])

  // Render content based on current state
  const renderContent = useCallback(() => {
    if (isConnected || state.status === 'connected') {
      return <ReconnectionSuccess displayName={displayName} />
    }

    if (state.showManualMessage || state.status === 'failed') {
      return (
        <ReconnectionManual
          displayName={displayName}
          onRetry={handleRetry}
          onClose={handleClose}
          error={state.error}
        />
      )
    }

    return (
      <ReconnectionProgress
        displayName={displayName}
        attempt={state.attempt}
        timeLeft={state.timeLeft}
        isReconnecting={state.isReconnecting}
        status={state.status}
      />
    )
  }, [isConnected, state, displayName, handleRetry, handleClose])

  // Don't render if not visible
  if (!visible) {
    return null
  }

  return (
    <Dialog {...dialogProps}>
      <div id="reconnection-dialog-description" className="reconnection-content">
        {renderContent()}
      </div>
    </Dialog>
  )
}

export default ReconnectionDialog
